/**
 * Enhanced vCard Generator Utility
 * Generates vCard 3.0 format with comprehensive contact information
 * Compatible with iOS (iPhone), Android, and desktop contacts apps
 */

/**
 * Convert image URL to base64 format for vCard PHOTO field with aggressive compression
 * @param {string} imageUrl - URL of the image
 * @param {number} maxSize - Maximum dimension (width/height) in pixels (default: 150)
 * @param {number} quality - JPEG quality 0-1 (default: 0.6)
 * @returns {Promise<string>} Base64 encoded image data
 */
const imageUrlToBase64 = async (imageUrl, maxSize = 150, quality = 0.6) => {
  try {
    // If no image URL provided, return empty string
    if (!imageUrl) return '';
    
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Calculate new dimensions (maintain aspect ratio)
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        
        // Create canvas and resize image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // Fill with white background for JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 JPEG
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          // Extract base64 data (remove data:image/jpeg;base64, prefix)
          const base64data = dataUrl.split(',')[1];
          resolve(base64data);
        } catch (e) {
          console.warn('Canvas toDataURL failed:', e);
          resolve('');
        }
      };
      
      img.onerror = () => {
        console.warn('Image failed to load:', imageUrl);
        resolve('');
      };
      
      // Handle data URLs directly
      if (imageUrl.startsWith('data:image')) {
        img.src = imageUrl;
      } else {
        // For external URLs, we need to handle CORS
        // Try with the image URL directly first
        img.src = imageUrl;
      }
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(''), 5000);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return '';
  }
};

/**
 * Escape special characters in vCard fields
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
const escapeVCardText = (text) => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')   // Escape backslash first
    .replace(/;/g, '\\;')      // Escape semicolon
    .replace(/,/g, '\\,')      // Escape comma
    .replace(/\n/g, '\\n');    // Escape newline
};

/**
 * Fold long lines in vCard (RFC 2426 requires lines to be max 75 chars)
 * @param {string} line - Line to fold
 * @returns {string} Folded line
 */
const foldLine = (line) => {
  if (line.length <= 75) return line;
  
  let result = '';
  let currentLine = '';
  
  for (let i = 0; i < line.length; i++) {
    currentLine += line[i];
    if (currentLine.length === 75 && i < line.length - 1) {
      result += currentLine + '\r\n ';
      currentLine = '';
    }
  }
  result += currentLine;
  
  return result;
};

/**
 * Generate a comprehensive vCard from card data
 * vCard 3.0 format for maximum compatibility with iOS and Android
 * @param {Object} card - Card object containing all contact information
 * @param {boolean} includePhoto - Whether to include photo (default: true)
 * @returns {Promise<string>} vCard string
 */
export const generateVCard = async (card, includePhoto = true) => {
  let vCardLines = [];
  
  // vCard header - use CRLF line endings for iOS compatibility
  vCardLines.push('BEGIN:VCARD');
  vCardLines.push('VERSION:3.0');
  
  // Product ID for identification
  vCardLines.push('PRODID:-//Piper Card//Digital Business Card//EN');
  
  // Full Name (Required) - FN must come before N for iOS
  if (card.name) {
    vCardLines.push(`FN:${escapeVCardText(card.name)}`);
    
    // N field (Family Name;Given Name;Additional Names;Honorific Prefixes;Honorific Suffixes)
    const nameParts = card.name.trim().split(' ');
    if (nameParts.length > 1) {
      const lastName = nameParts[nameParts.length - 1];
      const firstName = nameParts.slice(0, -1).join(' ');
      vCardLines.push(`N:${escapeVCardText(lastName)};${escapeVCardText(firstName)};;;`);
    } else {
      vCardLines.push(`N:${escapeVCardText(card.name)};;;;`);
    }
  } else {
    // Default name if not provided
    vCardLines.push('FN:Contact');
    vCardLines.push('N:Contact;;;;');
  }
  
  // Organization
  if (card.company) {
    vCardLines.push(`ORG:${escapeVCardText(card.company)}`);
  }
  
  // Title/Role
  if (card.title) {
    vCardLines.push(`TITLE:${escapeVCardText(card.title)}`);
  }
  
  // Phone number - use proper TYPE parameter format for iOS
  if (card.phone) {
    // Clean phone number
    const cleanPhone = card.phone.replace(/[^\d+]/g, '');
    vCardLines.push(`TEL;TYPE=CELL:${cleanPhone}`);
  }
  
  // Email - simplified format for better compatibility
  if (card.email) {
    vCardLines.push(`EMAIL:${card.email}`);
  }
  
  // Website - primary website
  if (card.website) {
    const website = card.website.startsWith('http') ? card.website : `https://${card.website}`;
    vCardLines.push(`URL:${website}`);
  } else if (card.websiteName && card.websiteTLD) {
    const website = `https://${card.websiteName}${card.websiteTLD}`;
    vCardLines.push(`URL:${website}`);
  }
  
  // Address - use locationAddress
  if (card.locationAddress || card.address) {
    const address = card.locationAddress || card.address || '';
    vCardLines.push(`ADR;TYPE=WORK:;;${escapeVCardText(address)};;;;`);
  }
  
  // Social Media as X-SOCIALPROFILE (iOS compatible) and NOTE
  const socialLinks = [];
  
  if (card.linkedin) {
    const linkedinUrl = card.linkedin.startsWith('http') 
      ? card.linkedin 
      : `https://linkedin.com/in/${card.linkedin}`;
    vCardLines.push(`X-SOCIALPROFILE;TYPE=linkedin:${linkedinUrl}`);
    socialLinks.push(`LinkedIn: ${linkedinUrl}`);
  }
  
  if (card.twitter) {
    const twitterUrl = card.twitter.startsWith('http') 
      ? card.twitter 
      : `https://twitter.com/${card.twitter.replace('@', '')}`;
    vCardLines.push(`X-SOCIALPROFILE;TYPE=twitter:${twitterUrl}`);
    socialLinks.push(`Twitter: ${twitterUrl}`);
  }
  
  if (card.instagram) {
    const instagramUrl = card.instagram.startsWith('http') 
      ? card.instagram 
      : `https://instagram.com/${card.instagram.replace('@', '')}`;
    vCardLines.push(`X-SOCIALPROFILE;TYPE=instagram:${instagramUrl}`);
    socialLinks.push(`Instagram: ${instagramUrl}`);
  }
  
  if (card.facebook) {
    const facebookUrl = card.facebook.startsWith('http') 
      ? card.facebook 
      : `https://facebook.com/${card.facebook}`;
    socialLinks.push(`Facebook: ${facebookUrl}`);
  }
  
  if (card.youtube) {
    const youtubeUrl = card.youtube.startsWith('http') 
      ? card.youtube 
      : `https://youtube.com/@${card.youtube}`;
    socialLinks.push(`YouTube: ${youtubeUrl}`);
  }
  
  if (card.github) {
    const githubUrl = card.github.startsWith('http') 
      ? card.github 
      : `https://github.com/${card.github}`;
    socialLinks.push(`GitHub: ${githubUrl}`);
  }
  
  if (card.whatsapp) {
    const cleanWhatsapp = card.whatsapp.replace(/[^0-9]/g, '');
    vCardLines.push(`TEL;TYPE=CELL,WHATSAPP:${cleanWhatsapp}`);
  }
  
  // Bio/Notes - include social links for Android compatibility
  let noteContent = '';
  if (card.bio) {
    noteContent = card.bio;
  }
  if (socialLinks.length > 0) {
    noteContent += (noteContent ? '\\n\\n' : '') + 'Social Links:\\n' + socialLinks.join('\\n');
  }
  if (noteContent) {
    vCardLines.push(`NOTE:${escapeVCardText(noteContent)}`);
  }
  
  // Profile Photo - use proper encoding for iOS and Android
  if (includePhoto && (card.profileImage || card.avatar)) {
    const imageUrl = card.profileImage || card.avatar;
    try {
      const base64Image = await imageUrlToBase64(imageUrl);
      if (base64Image && base64Image.length > 0) {
        // Use proper PHOTO format that works on both iOS and Android
        // iOS prefers ENCODING=b, Android prefers ENCODING=BASE64
        // Using ENCODING=b works on both
        const photoLine = `PHOTO;ENCODING=b;TYPE=JPEG:${base64Image}`;
        vCardLines.push(foldLine(photoLine));
      }
    } catch (error) {
      console.warn('Could not include photo in vCard:', error);
    }
  }
  
  // Revision timestamp
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  vCardLines.push(`REV:${timestamp}`);
  
  // End vCard
  vCardLines.push('END:VCARD');
  
  // Join with CRLF for proper vCard format (required by RFC 2426)
  return vCardLines.join('\r\n');
};

/**
 * Save contact directly to phone or download vCard file
 * @param {string} vCardContent - vCard string content
 * @param {string} fileName - File name without extension
 * @returns {Promise<boolean>} Returns true if shared/saved successfully
 */
export const downloadVCard = async (vCardContent, fileName) => {
  const cleanFileName = (fileName || 'contact').replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
  
  // Detect device type
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  
  try {
    // Create blob with proper MIME type
    const blob = new Blob([vCardContent], { type: 'text/vcard;charset=utf-8' });
    
    // Try Web Share API first (best for mobile)
    if (isMobile && navigator.share && navigator.canShare) {
      const file = new File([blob], `${cleanFileName}.vcf`, { type: 'text/vcard' });
      
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: cleanFileName,
          });
          return true;
        } catch (shareError) {
          // User cancelled or share failed, fall through to download method
          console.log('Share cancelled or failed, trying download method');
        }
      }
    }
    
    // Fallback: Direct download/open method
    if (isIOS) {
      // iOS method: Use data URI with specific MIME type
      // This triggers the iOS contacts import dialog
      const reader = new FileReader();
      reader.onload = function() {
        const dataUrl = reader.result;
        // Create link and click
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${cleanFileName}.vcf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Also try opening in new window for iOS Safari
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      };
      reader.readAsDataURL(blob);
      return true;
    } else if (isAndroid) {
      // Android method: Use blob URL
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cleanFileName}.vcf`;
      link.type = 'text/vcard';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      return true;
    } else {
      // Desktop method
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cleanFileName}.vcf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      return true;
    }
  } catch (error) {
    console.error('Error downloading vCard:', error);
    
    // Last resort fallback: Simple data URI
    try {
      const dataUri = `data:text/vcard;charset=utf-8,${encodeURIComponent(vCardContent)}`;
      const link = document.createElement('a');
      link.href = dataUri;
      link.download = `${cleanFileName}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch (e) {
      console.error('Fallback download also failed:', e);
      return false;
    }
  }
};

/**
 * Generate and download/share vCard in one function
 * @param {Object} card - Card object containing all contact information
 * @param {boolean} includePhoto - Whether to include photo (default: true)
 * @returns {Promise<boolean>} Returns true if shared/saved successfully
 */
export const generateAndDownloadVCard = async (card, includePhoto = true) => {
  try {
    const vCardContent = await generateVCard(card, includePhoto);
    const fileName = card.name || 'contact';
    return await downloadVCard(vCardContent, fileName);
  } catch (error) {
    console.error('Error generating vCard:', error);
    return false;
  }
};

export default {
  generateVCard,
  downloadVCard,
  generateAndDownloadVCard
};
